// Fixed value lists shared by the database, the API, the web app and (later) the mobile app.

/**
 * What an assistant panel was opened for, when it was opened for something in particular (it only adds a line to the
 * assistant's instructions): adding an insight, asking the Research Center, Quick capture, voice control.
 */
export const CONVERSATION_PURPOSES = ['insight', 'research', 'capture', 'voice'] as const;
export type ConversationPurpose = (typeof CONVERSATION_PURPOSES)[number];

/** Partner asks and sees reviewed answers. Director is the single reviewer. Member is the team. Admin is a separate permission. */
export const ROLES = ['partner', 'director', 'member'] as const;
export type Role = (typeof ROLES)[number];

export const ACTIVITY_TYPES = ['meeting', 'workshop', 'deliverable', 'task'] as const;
export type ActivityType = (typeof ACTIVITY_TYPES)[number];
export const TYPE_LABELS: Record<ActivityType, string> = { meeting: 'Meeting', workshop: 'Workshop', deliverable: 'Deliverable', task: 'Task' };

export const ACTIVITY_STATUSES = ['not_started', 'in_progress', 'at_risk', 'blocked', 'done'] as const;
export type ActivityStatus = (typeof ACTIVITY_STATUSES)[number];
export const STATUS_LABELS: Record<ActivityStatus, string> = {
  not_started: 'Not started', in_progress: 'In progress', at_risk: 'At risk', blocked: 'Blocked', done: 'Done',
};

export const ACTION_KINDS = ['create_activity', 'create_reminder', 'add_file', 'send_message', 'update_activity', 'file_minutes', 'confirm_answer', 'nudge_check', 'review_answer', 'update_context', 'log_email', 'start_research', 'follow_link', 'add_research_topic', 'check_in', 'add_insight',
  'create_space', 'create_track', 'set_member', 'remove_member', 'archive'] as const;
export type ActionKind = (typeof ACTION_KINDS)[number];
export const KIND_LABELS: Record<ActionKind, string> = {
  create_activity: 'New activity', create_reminder: 'Reminder', add_file: 'Add file',
  // Approving sends it by email (Amazon SES); where email is not connected, it is kept as a draft on the activity.
  send_message: 'Message',
  update_activity: 'Update activity', file_minutes: 'File minutes', confirm_answer: 'Confirm which is right', nudge_check: 'Nudge the team', check_in: 'Ask for an update',
  review_answer: 'Review the answer', update_context: 'Update context',
  // An email the assistant could not place: a person picks where it goes (Log it) or dismisses it.
  log_email: 'Email to log',
  start_research: 'Research', follow_link: 'Follow a link', add_research_topic: 'Research topic',
  add_insight: 'Add an insight',
  create_space: 'New space', create_track: 'New track', set_member: 'Space member', remove_member: 'Remove from space', archive: 'Archive',
};

/** Cards that cannot be approved as they are: a person first picks where they go (Log it), which turns them into a normal card. */
export const PLACE_FIRST: ReadonlySet<ActionKind> = new Set<ActionKind>(['log_email']);

/** Context suggestions come from minutes, a done milestone, an activity update, a logged email, or a new contact. */
export const ACTION_SOURCES = ['chat', 'email', 'recording', 'notetaker', 'gone_quiet', 'invite', 'team_check', 'minutes', 'milestone', 'activity_update', 'logged_email', 'new_contact', 'follow_up'] as const;
export type ActionSource = (typeof ACTION_SOURCES)[number];
export const SOURCE_LABELS: Record<ActionSource, string> = {
  chat: 'From chat', email: 'Forwarded email', recording: 'Recording', notetaker: 'Notetaker', gone_quiet: 'Gone quiet', invite: 'Invite', team_check: 'Team check', minutes: 'Meeting minutes',
  milestone: 'Milestone', activity_update: 'Activity update', logged_email: 'Email', new_contact: 'New contact', follow_up: 'Follow-up',
};

// ---------- Research Center ----------

/** DECISIONS, Research levels. */
export const RESEARCH_LEVELS = ['quick', 'standard', 'deep'] as const;
export type ResearchLevel = (typeof RESEARCH_LEVELS)[number];
export interface ResearchLevelInfo { name: string; time: string; sources: string; check: string; get: string; cost: string; costX: number; button: string; note: string; out: string }
export const LEVEL_INFO: Record<ResearchLevel, ResearchLevelInfo> = {
  quick: { name: 'Quick', time: 'A few minutes', sources: 'About 5 sources', check: 'Checks the sources it links', get: 'A short answer with links. Runs now', cost: 'Cost 1x', costX: 1,
    button: 'Start quick research', note: 'Runs now. The answer appears on the Research tab in a few minutes.', out: 'Answer in a few minutes' },
  standard: { name: 'Standard', time: 'About 15 minutes', sources: '15 to 25 sources', check: 'Every quote checked against its source', get: 'Results as insights. Runs now', cost: 'Cost about 5x', costX: 5,
    button: 'Start research', note: 'Runs now. Results arrive as new insights with a New badge, and you get a notification.', out: 'Results in about 15 min' },
  deep: { name: 'Deep', time: 'Up to an hour', sources: '50 or more sources', check: 'Claims cross-checked across sources', get: 'A written report plus insights. Runs in the background, notifies you', cost: 'Cost about 25x', costX: 25,
    button: 'Start deep research', note: 'Runs in the background. You get a notification when the report is ready.', out: 'Report within the hour' },
};
/** "go deeper": the next level up (Deep stays Deep). */
export const NEXT_LEVEL: Record<ResearchLevel, ResearchLevel> = { quick: 'standard', standard: 'deep', deep: 'deep' };

export const INSIGHT_CATEGORIES = ['Healthcare strategy', 'AI in healthcare news', 'Frameworks', 'Regulation', 'Clinical evidence', 'Operations'] as const;
export type InsightCategory = (typeof INSIGHT_CATEGORIES)[number];
export const FOLLOW_FREQUENCIES = ['daily', 'every_3_days', 'weekly'] as const;
export type FollowFrequency = (typeof FOLLOW_FREQUENCIES)[number];
export const FREQUENCY_LABELS: Record<FollowFrequency, string> = { daily: 'Daily', every_3_days: 'Every 3 days', weekly: 'Weekly' };
/** A quote checked by code against the text the research read: in it, in it but a number in the claim is not, or not there. */
export const QUOTE_CHECKS = ['match', 'number', 'not_found'] as const;
export type QuoteCheck = (typeof QUOTE_CHECKS)[number];
export const QUOTE_CHECK_LABELS: Record<QuoteCheck, string> = { match: 'Matches the source', number: 'Number not in source', not_found: 'Quote not found in the source' };
/**
 * How well an insight is backed (release 6.3): confirmed (two or more independent sources, distinct sites whose quotes
 * checked out), single (one), contested (it is in an open contradiction with another insight: sources disagree). A
 * person can set it by hand; until then it is worked out from the data.
 */
export const INSIGHT_CONFIDENCE = ['confirmed', 'single', 'contested'] as const;
export type InsightConfidence = (typeof INSIGHT_CONFIDENCE)[number];
export const CONFIDENCE_LABELS: Record<InsightConfidence, string> = { confirmed: 'Confirmed', single: 'Single source', contested: 'Contested' };

/** ignored: a person pressed Ignore (or said it). rejected: closed by the system (diverted, sent back). */
export const ACTION_STATUSES = ['waiting', 'approved', 'rejected', 'ignored'] as const;
export type ActionStatus = (typeof ACTION_STATUSES)[number];

export const NOTIFICATION_KINDS = ['reminder_due', 'approval_request', 'assigned', 'mention', 'due', 'question', 'knowledge', 'system'] as const;
export type NotificationKind = (typeof NOTIFICATION_KINDS)[number];

/**
 * What a person hears about, as listed in "You hear about" on Notifications. Each kind belongs to one category;
 * system notes (results, roll-ups) go with approvals. The app channel is always on: every notification is a row
 * that stays until dismissed. Push and email are per category.
 */
/**
 * The language people speak to the app in (the Speak button, Talk, Record): sent to the voice service as its
 * language code (ISO 639-3). English unless a person picks Arabic in Settings.
 */
export const SPEECH_LANGUAGES = ['eng', 'ara'] as const;
export type SpeechLanguage = (typeof SPEECH_LANGUAGES)[number];
export const SPEECH_LANGUAGE_LABELS: Record<SpeechLanguage, string> = { eng: 'English', ara: 'Arabic' };

export const NOTIFICATION_CATEGORIES = ['tagged', 'due', 'approvals', 'reminders', 'checkins', 'knowledge'] as const;
export type NotificationCategory = (typeof NOTIFICATION_CATEGORIES)[number];
export const NOTIFICATION_CATEGORY_LABELS: Record<NotificationCategory, string> = {
  tagged: 'Tagged as owner or mentioned', due: 'Something you own is due or at risk', approvals: 'Approvals waiting for you',
  reminders: 'Your reminders, when they come due', checkins: 'Readiness check-ins, 1 to 2 days before', knowledge: 'Research Center: new finding to verify',
};
export const CATEGORY_OF_KIND: Record<NotificationKind, NotificationCategory> = {
  assigned: 'tagged', mention: 'tagged', due: 'due', approval_request: 'approvals', system: 'approvals',
  reminder_due: 'reminders', question: 'checkins', knowledge: 'knowledge',
};
export const DELIVERY_CHANNELS = ['app', 'push', 'email'] as const;
export type DeliveryChannel = (typeof DELIVERY_CHANNELS)[number];
/** The defaults from the prototype's "You hear about" list. Quiet hours hold push and email, never the app row. */
export const DEFAULT_CHANNELS: Record<NotificationCategory, { push: boolean; email: boolean }> = {
  tagged: { push: true, email: false }, due: { push: true, email: true }, approvals: { push: true, email: false },
  reminders: { push: true, email: false }, checkins: { push: true, email: false }, knowledge: { push: false, email: false },
};
export const DEFAULT_QUIET_HOURS = { from: '20:00', to: '07:00' } as const;

/**
 * Context suggestions: update_context cards the assistant made by itself from what happened in the work (the five
 * automatic sources). They block nobody, so they are left out of every waiting count (the Requests badge, the
 * Assistant's "Waiting for you", the phone orb), are shown as one collapsible group per space, and never get a
 * notification each: at most one roll-up per person per day. A context change someone asked for in chat is not a
 * suggestion: it is counted and notified like any other card.
 */
export const SUGGESTION_SOURCES = ['minutes', 'milestone', 'activity_update', 'logged_email', 'new_contact'] as const;
export const isSuggestion = (a: { kind: string; source: string }): boolean =>
  a.kind === 'update_context' && (SUGGESTION_SOURCES as readonly string[]).includes(a.source);
/** The same rule in SQL, for a proposed_actions row aliased `alias`. */
export const SUGGESTION_SQL = (alias: string) =>
  `(${alias}.kind = 'update_context' and ${alias}.source in (${SUGGESTION_SOURCES.map(s => `'${s}'`).join(', ')}))`;

/** user: set by hand. assistant: set by approving an assistant card. Who set it is set_by_user. */
export const REMINDER_SET_BY = ['user', 'assistant'] as const;
export type ReminderSetBy = (typeof REMINDER_SET_BY)[number];

/** overdue: an earlier day. late: today and its time has passed. */
export const REMINDER_STATES = ['overdue', 'late', 'today', 'upcoming', 'done'] as const;
export type ReminderState = (typeof REMINDER_STATES)[number];

export const FILE_SOURCES = ['upload', 'assistant', 'email', 'recording'] as const;
export type FileSource = (typeof FILE_SOURCES)[number];

export const UPDATE_KINDS = ['update', 'minutes', 'system'] as const;
export type UpdateKind = (typeof UPDATE_KINDS)[number];
export const UPDATE_SOURCES = ['manual', 'assistant', 'email', 'recording', 'team_check'] as const;
export type UpdateSource = (typeof UPDATE_SOURCES)[number];

export const DEVICE_PLATFORMS = ['ios', 'web'] as const;
export type DevicePlatform = (typeof DEVICE_PLATFORMS)[number];

export const SESSION_CLIENTS = ['web', 'app'] as const;
export type SessionClient = (typeof SESSION_CLIENTS)[number];

export const SPACE_COLORS = ['grn', 'amb', 'red', 'acc'] as const;
export type SpaceColor = (typeof SPACE_COLORS)[number];
/** The colours a space can be given: violet ('acc') is the AI's colour only (Kareem, 4 Nov). */
export const SPACE_COLOR_CHOICES = ['grn', 'amb', 'red'] as const satisfies readonly SpaceColor[];
/**
 * The colour a space is shown in: a space made violet before is shown green, since red and amber also mean risk and
 * attention in the app. The stored value is not changed.
 */
export const shownSpaceColor = (c: SpaceColor | null | undefined): (typeof SPACE_COLOR_CHOICES)[number] => (!c || c === 'acc' ? 'grn' : c);

/** director: routed to the reviewer only (a partner's question the assistant could not answer). */
export const CHECK_AUDIENCES = ['director', 'person', 'owners', 'everyone'] as const;
export type CheckAudience = (typeof CHECK_AUDIENCES)[number];
export const CHECK_STATUSES = ['open', 'in_review', 'answered', 'conflict', 'confirmed', 'expired'] as const;
export type CheckStatus = (typeof CHECK_STATUSES)[number];

export const CALENDAR_KINDS = ['meeting', 'workshop', 'deliverable', 'task', 'reminder'] as const;
export type CalendarKind = (typeof CALENDAR_KINDS)[number];

/**
 * One permission table. Partners are read-and-ask: they read the spaces they belong to, use the assistant,
 * ask questions and set their own reminders; they do not create, edit, delete or own activities, and do not
 * add tracks, contacts or files. Relax a partner permission by adding 'partner' to its list.
 */
export const PERMISSIONS = {
  'activity.write': ['director', 'member'],
  'activity.own': ['director', 'member'],
  'track.write': ['director', 'member'],
  'contact.write': ['director', 'member'],
  'file.write': ['director', 'member'],
  'reminder.write': ['partner', 'director', 'member'],
  'check.ask': ['partner', 'director', 'member'],
  'assistant.use': ['partner', 'director', 'member'],
  /**
   * Every space: view and edit everything in every space, whatever the membership, ownership or lead: spaces,
   * tracks, activities (owners, delete, restore), files, context and proposed actions. Two things stay out:
   * other people's personal reminders (setting a reminder for anyone is allowed), and partner-question review,
   * which is the director's anyway. Admins also see every space (they manage people and spaces).
   */
  'space.all': ['director'],
  /** Space and Track context: proposing a change through the assistant. Editing is space.all, an admin, or a lead. */
  'context.propose': ['director', 'member'],
} as const satisfies Record<string, readonly Role[]>;
export type Permission = keyof typeof PERMISSIONS;
export const can = (role: Role, p: Permission): boolean => (PERMISSIONS[p] as readonly Role[]).includes(role);

export const SESSION_LIFETIME_DAYS: Record<SessionClient, number> = { web: 30, app: 90 };
export const SESSION_IDLE_DAYS: Record<SessionClient, number> = { web: 7, app: 30 };

// ---------- Space and Track context ----------
/**
 * One living context doc per Space and per Track: the engagement facts the team and the assistant work from.
 * Not the knowledge base (cross-engagement insights), and never a place for long documents (those stay in Files).
 */
export const CONTEXT_SECTIONS = ['background', 'scope', 'people', 'constraints', 'decisions', 'terms', 'fit'] as const;
export type ContextSection = (typeof CONTEXT_SECTIONS)[number];
export const SPACE_CONTEXT_SECTIONS: readonly ContextSection[] = ['background', 'scope', 'people', 'constraints', 'decisions', 'terms'];
export const TRACK_CONTEXT_SECTIONS: readonly ContextSection[] = ['background', 'scope', 'constraints', 'decisions', 'terms', 'fit'];
export const CONTEXT_SECTION_LABELS: Record<ContextSection, string> = {
  background: 'Background and goals', scope: 'Scope (in / out)', people: "Who's who", constraints: 'Constraints',
  decisions: 'Key decisions', terms: 'Terms the client uses', fit: 'How this track fits the space',
};
export type ContextTargetKind = 'space' | 'track';
export const sectionsFor = (kind: ContextTargetKind) => (kind === 'space' ? SPACE_CONTEXT_SECTIONS : TRACK_CONTEXT_SECTIONS);
/** Per doc (all sections together): about two pages. */
export const CONTEXT_MAX_CHARS = 12_000;
/** One assistant proposal adds or replaces at most this much: long material belongs in Files. */
export const CONTEXT_PROPOSAL_MAX_CHARS = 2_000;

// ---------- Knowledge base (document folders) ----------
/** reading: text and summary not done yet. unreadable: password protected, damaged or a type we cannot read (with a reason). */
export const FILE_STATUSES = ['reading', 'ready', 'unreadable'] as const;
export type FileStatus = (typeof FILE_STATUSES)[number];
/** What a reference means in a file: a page (PDF, Word), a sheet (Excel), a slide (PowerPoint), a section (text, CSV). */
export const FILE_UNITS = ['page', 'sheet', 'slide', 'section'] as const;
export type FileUnit = (typeof FILE_UNITS)[number];
/** Knowledge base uploads: at most this size. */
export const KB_MAX_BYTES = 50 * 1024 * 1024;
/** Knowledge base uploads: these types only (by extension; the browser's type is checked against it). */
export const KB_TYPES: Record<string, { mime: string; label: string }> = {
  pdf: { mime: 'application/pdf', label: 'PDF' },
  docx: { mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', label: 'Word' },
  xlsx: { mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', label: 'Excel' },
  pptx: { mime: 'application/vnd.openxmlformats-officedocument.presentationml.presentation', label: 'PowerPoint' },
  png: { mime: 'image/png', label: 'Image' },
  jpg: { mime: 'image/jpeg', label: 'Image' },
  jpeg: { mime: 'image/jpeg', label: 'Image' },
  webp: { mime: 'image/webp', label: 'Image' },
  gif: { mime: 'image/gif', label: 'Image' },
  txt: { mime: 'text/plain', label: 'Text' },
  csv: { mime: 'text/csv', label: 'CSV' },
};
export const extOf = (name: string): string => (name.match(/\.([A-Za-z0-9]{1,8})$/)?.[1] ?? '').toLowerCase();
/** The Knowledge base type of a file name, or null when it is not one we take. */
export const kbType = (name: string) => KB_TYPES[extOf(name)] ?? null;
